import { useContext, useState } from "react";
import { startAuthentication } from "@simplewebauthn/browser";

import { Button } from "components/DesignSystem/Button/Button";
import { Passkey } from "components/icons/Passkey";
import {
  PASSKEY_SIGNIN_CLICKED,
  PASSKEY_SIGNIN_FAILED,
  PASSKEY_SIGNIN_SUCCESS,
} from "constants/analyticsEvents";
import authContext from "jwt_context&axios/authContext";
import { useAnalytics } from "hooks/useAnalytics";
import { useToast } from "hooks/useToast";
import { browserSupportsPasskey } from "utils/browserSupportsPasskey";
import { humanizeWebAuthnError } from "utils/humanizeWebAuthnError";
import { BackendError } from "types/utils/error";
import {
  useWebauthnLoginBeginMutation,
  useWebauthnLoginFinishMutation,
} from "store/apis/webauthn";

type WebAuthnSignInButtonProps = {
  acceptTos: boolean;
};

export const WebAuthnSignInButton = ({
  acceptTos,
}: WebAuthnSignInButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { updateSigninData } = useContext(authContext);
  const { alertToast } = useToast();
  const { trackEvent } = useAnalytics();

  const [webauthnLoginBegin] = useWebauthnLoginBeginMutation();
  const [webauthnLoginFinish] = useWebauthnLoginFinishMutation();

  if (!browserSupportsPasskey()) {
    return null;
  }

  const signInWithPasskey = async () => {
    setIsLoading(true);
    try {
      const options = await webauthnLoginBegin().unwrap();
      const credential = await startAuthentication({ optionsJSON: options });
      const result = await webauthnLoginFinish({
        credential,
        accept_tos: acceptTos,
      }).unwrap();
      trackEvent(PASSKEY_SIGNIN_SUCCESS);
      updateSigninData({ data: { data: result } });
    } catch (error) {
      trackEvent(PASSKEY_SIGNIN_FAILED);
      const backendMessage = (error as BackendError)?.data?.error?.message;
      alertToast(
        { message: backendMessage || humanizeWebAuthnError(error) },
        error as object
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    trackEvent(PASSKEY_SIGNIN_CLICKED);
    void signInWithPasskey();
  };

  return (
    <Button
      block
      size="regular"
      onClick={handleClick}
      disabled={isLoading}
      isLoading={isLoading}
      leftIcon={<Passkey />}
    >
      Sign in with passkey
    </Button>
  );
};
